import { Core } from "@faire/design-tokens";
import { SymbolDiv } from "@faire/retailer-visitor-shared/components/Button/RoundButtonStyles";
import { IRoundQuantityPickerProps } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker";
import { IRoundQuantityPickerStateProps } from "@faire/retailer-visitor-shared/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { getSpacing } from "@faire/web/slate/spacing";
import { Span } from "@faire/web/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

export const RoundMultiOptionPicker: React.FC<
  Pick<
    IRoundQuantityPickerProps & IRoundQuantityPickerStateProps,
    "value" | "onChooseMultiOption" | "size" | "maxWidth"
  > & {
    textWidth: number;
  }
> = ({ value, onChooseMultiOption, size, maxWidth, textWidth }) => {
  const { strictLocalize } = useStrictLocalization();
  const componentSize = getSpacing(size ?? "4x");

  let valueDisplay = null;
  if (value === 0) {
    valueDisplay = (
      <SymbolDiv data-test-id="round-multi-option-picker-add-symbol">
        <Add />
      </SymbolDiv>
    );
  } else {
    valueDisplay = (
      <Span variant="paragraphSansMedium" color="inherit" truncate>
        {value}
      </Span>
    );
  }

  const buttonOnClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    onChooseMultiOption?.();
  };

  return (
    <ButtonBase
      data-test-id="round-multi-option-picker"
      $isBase={value === 0}
      $textWidth={textWidth}
      $size={componentSize}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
        buttonOnClick(e)
      }
      $maxWidth={maxWidth ?? 0}
      aria-label={strictLocalize({
        defaultMessage: "Open quick add modal",
        description: {
          img: "https://cdn.faire.com/fastly/17cf5e3580c883659f843aaf151fa3cbc0edffb8b131cb81c8d911c3a98e5f02.png",
          text: "A line of text for user to lick and open to choose multi option",
        },
      })}
    >
      {valueDisplay}
    </ButtonBase>
  );
};

const ButtonBase = styled.button<{
  $size: string;
  $isBase: boolean;
  $maxWidth: number;
  $textWidth: number;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  height: ${(props) => props.$size};
  background-color: ${(props) =>
    props.$isBase ? Core.surface.inverse : Core.surface.default};
  transition: all 150ms linear;
  width: ${(props) =>
    props.$isBase
      ? props.$size
      : css`
          ${props.$textWidth}px
        `};
  border: 1px solid;
  border-color: ${({ $isBase }) =>
    $isBase ? Core.border.muted : Core.border.default};
  &:hover {
    border-color: ${Core.border.default};
  }
  border-radius: 16px;
  color: ${(props) =>
    props.$isBase ? Core.surface.default : Core.border.muted};
  ${({ $isBase }) =>
    $isBase
      ? css`
          &:hover {
            background-color: ${Core.surface.default};
            color: ${Core.border.muted};
          }
        `
      : null};
`;
